import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { Select, type SelectProps } from 'antd';
import {
  getActivityNameAndId,
  type GetActivityNameAndIdType,
  transformActivityNameAndId,
} from 'crm-atoms/queries';
import { useEffect, useMemo } from 'react';

export interface ActivitySelectProps extends Omit<SelectProps, 'options'> {
  transformOptions?: (
    options: ReturnType<typeof transformActivityNameAndId>
  ) => ReturnType<typeof transformActivityNameAndId>;
  autoSelectFirstOption?: boolean;
}

export default function ActivitySelect({
  onChange,
  value,
  transformOptions,
  autoSelectFirstOption,
  ...props
}: ActivitySelectProps) {
  const {
    data,
    isLoading,
  } = useGetGraphqlDataQuery<UseQueryResult<GetActivityNameAndIdType>>({
    document: getActivityNameAndId,
  });

  const filteredOptions = useMemo(() => {
    const options = transformActivityNameAndId(data);

    return transformOptions ? transformOptions(options) : options;
  }, [data, transformOptions]);

  useEffect(() => {
    if (
      !value
      && autoSelectFirstOption
      && filteredOptions.length > 0
      && onChange
      && filteredOptions[0]
    ) {
      onChange(filteredOptions[0].value, filteredOptions[0]);
    }
  }, [autoSelectFirstOption, filteredOptions, onChange, value]);

  return (
    <Select
      value={value}
      onChange={onChange}
      options={filteredOptions}
      loading={isLoading}
      optionFilterProp="label"
      {...props}
    />
  );
}
